import { useCallback, useEffect, useMemo, useState } from 'react'
import { App as AntdApp, Button, Card, Dropdown, Empty, Flex, InputNumber, Modal, Space, Table, Tabs, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EllipsisOutlined, HistoryOutlined, KeyOutlined, RedoOutlined, ReloadOutlined, SettingOutlined, WalletOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import type { ApiKeyRecord, BillingTariff } from '../../shared/services/api'
import { getApiService } from '../../shared/services/api'
import { TopUpAccountModal } from '../../features/accounts/components/TopUpAccountModal'
import { MetricCard } from '../../shared/ui/MetricCard'
import { PageHeader } from '../../shared/ui/PageHeader'
import { formatMoney } from '../../shared/utils/money'
import { formatInAppTimezone } from '../../shared/utils/timezone'
import { pluralRu } from '../../shared/utils/plural'

type AccountsTab = 'active' | 'archive'

export function AccountsPage() {
  const { message } = AntdApp.useApp()
  const api = useMemo(() => getApiService(), [])
  const navigate = useNavigate()
  const [keys, setKeys] = useState<ApiKeyRecord[]>([])
  const [tariff, setTariff] = useState<BillingTariff | null>(null)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<AccountsTab>('active')
  const [topUpAccount, setTopUpAccount] = useState<ApiKeyRecord | null>(null)
  const [retryingId, setRetryingId] = useState<number | null>(null)
  const [tariffOpen, setTariffOpen] = useState(false)
  const [tariffPrice, setTariffPrice] = useState<number | null>(null)
  const [tariffSaving, setTariffSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [keysResponse, tariffResponse] = await Promise.all([api.listApiKeys(), api.billingTariff()])
      setKeys(keysResponse.data.keys)
      setTariff(tariffResponse.data.tariff)
    } catch {
      message.error('Не удалось загрузить счета')
    } finally {
      setLoading(false)
    }
  }, [api, message])

  useEffect(() => { void load() }, [load])

  const activeAccounts = keys.filter((key) => key.isActive)
  const archivedAccounts = keys.filter((key) => !key.isActive)
  const visibleAccounts = activeTab === 'active' ? activeAccounts : archivedAccounts
  const currency = tariff?.currency ?? keys[0]?.balance.currency ?? 'USD'
  const confirmed = activeAccounts.reduce((sum, key) => sum + key.balance.confirmed, 0)
  const reserved = activeAccounts.reduce((sum, key) => sum + key.balance.reserved, 0)
  const available = activeAccounts.reduce((sum, key) => sum + key.balance.available, 0)

  const retryInsufficient = async (account: ApiKeyRecord) => {
    setRetryingId(account.id)
    try {
      const response = await api.retryInsufficientBalance(account.id)
      message.success(`Возвращено в очередь: ${response.data.retried}. Пока без средств: ${response.data.remaining}.`)
      await load()
    } catch {
      message.error('Не удалось повторить отправки')
    } finally {
      setRetryingId(null)
    }
  }

  const columns: ColumnsType<ApiKeyRecord> = [
    {
      title: 'Счёт приложения',
      dataIndex: 'name',
      width: 220,
      render: (name: string, account) => (
        <Flex vertical gap={2}>
          <Link className="account-name-link" to={`/accounts/${account.id}/transactions`}>{name}</Link>
          <Typography.Text type="secondary">Счёт №{account.id}</Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Доступно',
      dataIndex: ['balance', 'available'],
      align: 'right',
      width: 140,
      render: (_value, account) => <Typography.Text className="account-money account-money-primary" strong>{formatMoney(account.balance.available, account.balance.currency)}</Typography.Text>,
    },
    {
      title: 'В резерве',
      dataIndex: ['balance', 'reserved'],
      align: 'right',
      width: 130,
      render: (_value, account) => account.balance.reserved > 0
        ? <Typography.Text className="account-money account-money-reserved">{formatMoney(account.balance.reserved, account.balance.currency)}</Typography.Text>
        : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Баланс',
      dataIndex: ['balance', 'confirmed'],
      align: 'right',
      width: 140,
      render: (_value, account) => <Typography.Text className="account-money">{formatMoney(account.balance.confirmed, account.balance.currency)}</Typography.Text>,
    },
    {
      title: 'Последняя отправка',
      dataIndex: 'lastUsedAt',
      width: 150,
      render: (value: string | null) => value ? formatInAppTimezone(value, 'DD.MM.YYYY HH:mm') : <Typography.Text type="secondary">Ещё не было</Typography.Text>,
    },
    {
      title: 'Состояние',
      dataIndex: 'isActive',
      width: 105,
      render: (isActive: boolean) => isActive ? <Tag color="success">Активен</Tag> : <Tag>В архиве</Tag>,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 225,
      align: 'right',
      render: (_value, account) => (
        <Flex justify="flex-end" align="center" gap={2} wrap={false}>
          {account.isActive && <Button type="link" size="small" icon={<WalletOutlined />} onClick={() => setTopUpAccount(account)}>Пополнить</Button>}
          <Button type="link" size="small" icon={<HistoryOutlined />} onClick={() => navigate(`/accounts/${account.id}/transactions`)}>Операции</Button>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                ...(account.isActive ? [{ key: 'retry', icon: <RedoOutlined />, label: 'Повторить письма без средств' }] : []),
                { key: 'settings', icon: <KeyOutlined />, label: 'Настройки ключа' },
              ],
              onClick: ({ key }) => {
                if (key === 'retry') void retryInsufficient(account)
                if (key === 'settings') navigate('/api-keys')
              },
            }}
          >
            <Button
              className="accounts-row-menu"
              type="text"
              icon={<EllipsisOutlined />}
              loading={retryingId === account.id}
              aria-label={`Дополнительные действия счёта ${account.name}`}
            />
          </Dropdown>
        </Flex>
      ),
    },
  ]

  const saveTariff = async () => {
    if (tariffPrice === null || tariffPrice < 0) return
    setTariffSaving(true)
    try {
      const response = await api.updateBillingTariff(tariffPrice)
      setTariff(response.data.tariff)
      setTariffOpen(false)
      message.success('Стоимость письма обновлена')
    } catch {
      message.error('Не удалось обновить стоимость письма')
    } finally {
      setTariffSaving(false)
    }
  }

  return (
    <Flex className="accounts-page" vertical gap="middle">
      <PageHeader
        title="Счета"
        description="Баланс приложений, пополнения и списания за отправленные письма."
        icon={<WalletOutlined />}
        extra={(
          <Space size="small" wrap>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>Обновить</Button>
            <Button icon={<SettingOutlined />} onClick={() => { setTariffPrice(tariff?.pricePerEmail ?? 0); setTariffOpen(true) }}>Стоимость письма</Button>
          </Space>
        )}
      />

      <div className="metric-grid">
        <MetricCard title="Доступно" value={formatMoney(available, currency)} detail="Можно использовать для новых отправок" tone="green" />
        <MetricCard title="В резерве" value={formatMoney(reserved, currency)} detail="Письма ожидают окончательного результата" tone="amber" />
        <MetricCard title="Общий баланс" value={formatMoney(confirmed, currency)} detail={`${activeAccounts.length} ${pluralRu(activeAccounts.length, 'активный счёт', 'активных счёта', 'активных счетов')}`} tone="blue" />
        <MetricCard title="Стоимость письма" value={formatMoney(tariff?.pricePerEmail ?? 0, currency)} detail={tariff ? `Действует с ${formatInAppTimezone(tariff.createdAt, 'DD.MM.YYYY')}` : 'Тариф не загружен'} tone="neutral" />
      </div>

      <Card className="table-card accounts-workbench">
        <Tabs
          className="accounts-tabs"
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as AccountsTab)}
          items={[
            { key: 'active', label: `Активные (${activeAccounts.length})` },
            { key: 'archive', label: `Архив (${archivedAccounts.length})` },
          ]}
        />
        <Table<ApiKeyRecord>
          rowKey="id"
          className="accounts-table"
          columns={columns}
          dataSource={visibleAccounts}
          loading={loading}
          pagination={false}
          scroll={{ x: 1110 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={activeTab === 'active' ? 'Активных счетов пока нет' : 'Архив пуст'}
              >
                {activeTab === 'active' && <Button type="primary" onClick={() => navigate('/api-keys')}>Добавить приложение</Button>}
              </Empty>
            ),
          }}
          summary={() => visibleAccounts.length ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}><Typography.Text strong>Итого</Typography.Text></Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right"><Typography.Text strong>{formatMoney(visibleAccounts.reduce((sum, account) => sum + account.balance.available, 0), currency)}</Typography.Text></Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">{formatMoney(visibleAccounts.reduce((sum, account) => sum + account.balance.reserved, 0), currency)}</Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">{formatMoney(visibleAccounts.reduce((sum, account) => sum + account.balance.confirmed, 0), currency)}</Table.Summary.Cell>
              <Table.Summary.Cell index={4} colSpan={3} />
            </Table.Summary.Row>
          ) : null}
        />
      </Card>

      <TopUpAccountModal
        account={topUpAccount}
        onCancel={() => setTopUpAccount(null)}
        onSubmit={async (id, amount, comment) => {
          try {
            await api.topUpApiKey(id, amount, comment)
            message.success('Счёт пополнен')
            setTopUpAccount(null)
            await load()
          } catch (error) {
            message.error('Не удалось пополнить счёт')
            throw error
          }
        }}
      />

      <Modal
        open={tariffOpen}
        title="Стоимость одного письма"
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={tariffSaving}
        onCancel={() => setTariffOpen(false)}
        onOk={() => void saveTariff()}
      >
        <Flex vertical gap="small">
          <Typography.Text type="secondary">Новая стоимость применяется только к новым письмам.</Typography.Text>
          <InputNumber min={0} precision={6} value={tariffPrice} onChange={setTariffPrice} addonAfter={currency} style={{ width: '100%' }} />
        </Flex>
      </Modal>
    </Flex>
  )
}
